/**
 * Finance categories — a 3-level tree management screen. The API returns a
 * flat list with `parentId`; the tree is assembled client-side. Each node
 * supports expand/collapse, an active toggle, add-child (up to depth 3),
 * rename, and archive (handling the 409 `category_in_use` clearly).
 *
 * Income and expense live as two separate roots-of-roots so a tree can never
 * mix kinds — child kind always equals parent kind (enforced server-side too).
 */

import { useMemo, useState } from 'react';
import {
  useFinanceCategories,
  useCreateFinanceCategory,
  usePatchFinanceCategory,
  useArchiveFinanceCategory,
  type FinanceCategoryDto,
} from '../../api/finance';
import type { FinanceCategoryKind } from '@k-os/core';
import { ApiError } from '../../api/client';
import { FormModal } from '../../components/FormModal';
import { ConfirmDialog } from '../../components/finance/ConfirmDialog';
import {
  CATEGORY_KIND_LABELS,
  financeErrorCode,
  financeErrorMessage,
  buildCategoryTree,
  type CategoryTreeNode,
} from './finance-format';
import { Icon } from '@k-os/ui';
import formStyles from '../../components/FormModal.module.css';
import styles from './finance.module.css';

// Re-export the shared node type under the local alias used throughout this file.
type TreeNode = CategoryTreeNode;

const MAX_DEPTH = 3; // 1-based depth: root=1, child=2, grandchild=3

export function FinanceCategoriesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const categoriesQ = useFinanceCategories({ includeInactive });
  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data]);

  const trees = useMemo(() => buildCategoryTree(categories), [categories]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Modal state: create (optionally under a parent), or rename an existing one.
  const [form, setForm] = useState<
    | { mode: 'create'; kind: FinanceCategoryKind; parent: FinanceCategoryDto | null }
    | { mode: 'rename'; cat: FinanceCategoryDto }
    | null
  >(null);
  const [archiving, setArchiving] = useState<FinanceCategoryDto | null>(null);

  const renderNodes = (nodes: TreeNode[]): React.ReactNode =>
    nodes.map((node) => {
      const { cat, depth, children } = node;
      const hasChildren = children.length > 0;
      const isOpen = expanded.has(cat.id);
      const canAddChild = depth < MAX_DEPTH;
      return (
        <div key={cat.id}>
          <div
            className={`${styles.treeRow} ${cat.active ? '' : styles.inactive}`}
            style={{ paddingLeft: 14 + (depth - 1) * 22 }}
          >
            {hasChildren ? (
              <button
                type="button"
                className={styles.twisty}
                onClick={() => toggle(cat.id)}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                <Icon name={isOpen ? 'chevronDown' : 'chevron'} size={13} />
              </button>
            ) : (
              <span className={styles.twistySpacer} />
            )}
            <span className={styles.treeName}>{cat.name}</span>
            {depth === 1 && (
              <span
                className={`${styles.badge} ${
                  cat.kind === 'income' ? styles.badgeIncome : styles.badgeExpense
                }`}
              >
                {CATEGORY_KIND_LABELS[cat.kind]}
              </span>
            )}
            {cat.archivedAt && <span className={styles.badge}>archived</span>}
            <span className={styles.treeSpacer} />
            <div className={styles.treeActions}>
              <ActiveToggle cat={cat} />
              {canAddChild && !cat.archivedAt && (
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() =>
                    setForm({ mode: 'create', kind: cat.kind, parent: cat })
                  }
                >
                  + Subcategory
                </button>
              )}
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setForm({ mode: 'rename', cat })}
              >
                Rename
              </button>
              {!cat.archivedAt && (
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                  onClick={() => setArchiving(cat)}
                >
                  Archive
                </button>
              )}
            </div>
          </div>
          {hasChildren && isOpen && renderNodes(children)}
        </div>
      );
    });

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Categories</h2>
          <div className={styles.subtitle}>
            Up to three levels per income / expense tree. Rules and the agent
            categorize against these; archiving is blocked while a category (or
            any descendant) is still in use.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.toggle} ${includeInactive ? styles.toggleOn : ''}`}
            onClick={() => setIncludeInactive((v) => !v)}
          >
            {includeInactive ? 'Showing inactive' : 'Show inactive'}
          </button>
          <button
            type="button"
            className="kos-btn"
            onClick={() => setForm({ mode: 'create', kind: 'income', parent: null })}
          >
            + Income root
          </button>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => setForm({ mode: 'create', kind: 'expense', parent: null })}
          >
            + Expense root
          </button>
        </div>
      </header>

      {categoriesQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : categoriesQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(categoriesQ.error)}</div>
      ) : trees.length === 0 ? (
        <div className={styles.empty}>
          No categories yet. Add an income or expense root to get started.
        </div>
      ) : (
        <div className={styles.tree}>{renderNodes(trees)}</div>
      )}

      <CategoryFormModal form={form} onClose={() => setForm(null)} />
      <ArchiveCategoryDialog category={archiving} onClose={() => setArchiving(null)} />
    </div>
  );
}

// ─── Active toggle ───────────────────────────────────────────────────────────

function ActiveToggle({ cat }: { cat: FinanceCategoryDto }) {
  const patch = usePatchFinanceCategory();
  return (
    <button
      type="button"
      className={`${styles.toggle} ${cat.active ? styles.toggleOn : ''}`}
      disabled={patch.isPending}
      onClick={() => patch.mutate({ id: cat.id, patch: { active: !cat.active } })}
      title={cat.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
    >
      {cat.active ? 'Active' : 'Inactive'}
    </button>
  );
}

// ─── Create / rename modal ─────────────────────────────────────────────────────

type CategoryForm =
  | { mode: 'create'; kind: FinanceCategoryKind; parent: FinanceCategoryDto | null }
  | { mode: 'rename'; cat: FinanceCategoryDto };

function CategoryFormModal({
  form,
  onClose,
}: {
  form: CategoryForm | null;
  onClose: () => void;
}) {
  const create = useCreateFinanceCategory();
  const patch = usePatchFinanceCategory();

  const open = !!form;
  const [name, setName] = useState('');
  const [seen, setSeen] = useState<string | null>(null);

  const key = open
    ? form.mode === 'rename'
      ? `rename:${form.cat.id}`
      : `create:${form.kind}:${form.parent?.id ?? 'root'}`
    : null;
  if (open && key !== seen) {
    setSeen(key);
    setName(form.mode === 'rename' ? form.cat.name : '');
    create.reset();
    patch.reset();
  }
  if (!open && seen !== null) setSeen(null);

  const pending = create.isPending || patch.isPending;
  const trimmed = name.trim();
  const valid = trimmed.length > 0;
  const error = financeErrorMessage(create.error ?? patch.error);

  const title =
    !form
      ? ''
      : form.mode === 'rename'
        ? 'Rename category'
        : form.parent
          ? `New subcategory of “${form.parent.name}”`
          : `New ${CATEGORY_KIND_LABELS[form.kind].toLowerCase()} root`;

  const submit = () => {
    if (!form || !valid || pending) return;
    if (form.mode === 'rename') {
      patch.mutate({ id: form.cat.id, patch: { name: trimmed } }, { onSuccess: onClose });
    } else {
      create.mutate(
        {
          name: trimmed,
          kind: form.kind,
          parentId: form.parent?.id ?? null,
        },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={title}
      submitLabel={pending ? 'Saving…' : 'Save'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="cat-name">
          Name
        </label>
        <input
          id="cat-name"
          className={formStyles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder="e.g. Groceries"
        />
      </div>
    </FormModal>
  );
}

// ─── Archive confirm ─────────────────────────────────────────────────────────

function ArchiveCategoryDialog({
  category,
  onClose,
}: {
  category: FinanceCategoryDto | null;
  onClose: () => void;
}) {
  const archive = useArchiveFinanceCategory();
  const open = !!category;

  const [seen, setSeen] = useState<string | null>(null);
  if (open && category.id !== seen) {
    setSeen(category.id);
    archive.reset();
  }
  if (!open && seen !== null) setSeen(null);

  const err = archive.error;
  const conflict =
    err instanceof ApiError && err.status === 409 && financeErrorCode(err) === 'category_in_use';

  const categoryName = category?.name ?? '';
  const message = conflict
    ? `“${categoryName}” (or one of its subcategories) is still attached to transactions, budgets, or rules, so it can’t be archived. Reassign or remove those first.`
    : `Archive “${categoryName}”? It stays in historical reports but drops out of the active tree and pickers.`;

  return (
    <ConfirmDialog
      open={open}
      title={conflict ? 'Category still in use' : 'Archive category'}
      message={message}
      confirmLabel={archive.isPending ? 'Archiving…' : 'Archive'}
      confirmDisabled={archive.isPending || conflict}
      destructive
      hideConfirm={conflict}
      error={!conflict ? financeErrorMessage(err) : null}
      onClose={onClose}
      onConfirm={() => {
        if (!category) return;
        archive.mutate(category.id, { onSuccess: onClose });
      }}
    />
  );
}

